import { useState } from "react";
import {
  normalizeSlotLabel,
  normalizeSlotLabelsCsv,
  sanitizeSlotLabelInput,
} from "@/lib/manufacturing/slotLabel";
import { orderSlotLabelSuggestions } from "@/lib/manufacturing/slotLabelSuggestions";
import { mfgInlineInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

/** Free-text CSV slots plus chips. Priority slots show first; rest behind expand. */
export function SlotLabelsPicker({
  value,
  onChange,
  suggestions,
  prioritySuggestions = [],
  placeholder = "Slot labels (comma-sep or pick below)",
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  suggestions: readonly string[];
  /** Raised to the top (e.g. slots from the example recipe). */
  prioritySuggestions?: readonly string[];
  placeholder?: string;
  disabled?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const selected = new Set(
    value
      .split(",")
      .map((s) => normalizeSlotLabel(s))
      .filter(Boolean),
  );
  const ordered = orderSlotLabelSuggestions(suggestions, prioritySuggestions);
  const visible = expanded
    ? [...ordered.priority, ...ordered.rest]
    : ordered.priority.length > 0
      ? ordered.priority
      : ordered.rest;
  const hiddenCount =
    ordered.priority.length > 0 && !expanded ? ordered.rest.length : 0;

  function toggle(label: string) {
    const next = new Set(selected);
    const key = normalizeSlotLabel(label);
    if (!key) return;
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange([...next].join(", "));
  }

  return (
    <div className="space-y-2">
      <input
        className={`${mfgInlineInputClass} w-full min-w-[180px]`}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value
            .split(",")
            .map((part) => sanitizeSlotLabelInput(part))
            .join(", ");
          onChange(next);
        }}
        onBlur={() => {
          const n = normalizeSlotLabelsCsv(value);
          if (n !== value) onChange(n);
        }}
        autoComplete="off"
        spellCheck={false}
      />
      {visible.length > 0 || ordered.rest.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {visible.map((label) => {
            const key = normalizeSlotLabel(label) || label;
            const active = selected.has(key);
            const isPriority = ordered.priority.includes(key);
            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                onClick={() => toggle(label)}
                className={
                  "rounded-md px-2 py-0.5 text-xs font-medium transition-colors " +
                  (active
                    ? "bg-gray-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                    : isPriority
                      ? "bg-indigo-50 text-indigo-800 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-200 dark:hover:bg-indigo-900/40"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700")
                }
              >
                {key}
              </button>
            );
          })}
          {hiddenCount > 0 ? (
            <button
              type="button"
              disabled={disabled}
              onClick={() => setExpanded(true)}
              className="rounded-md px-2 py-0.5 text-xs font-medium text-gray-600 underline-offset-2 hover:underline dark:text-zinc-400"
              title={`Show ${hiddenCount} more org slot labels`}
            >
              … +{hiddenCount}
            </button>
          ) : null}
          {expanded && ordered.priority.length > 0 && ordered.rest.length > 0 ? (
            <button
              type="button"
              disabled={disabled}
              onClick={() => setExpanded(false)}
              className="rounded-md px-2 py-0.5 text-xs font-medium text-gray-600 underline-offset-2 hover:underline dark:text-zinc-400"
            >
              Show less
            </button>
          ) : null}
        </div>
      ) : (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">
          No org slot labels yet — type them above (they appear as chips after BOM lines use them).
        </p>
      )}
    </div>
  );
}
